import React, { useState, useRef, useEffect } from 'react';

/**
 * OptimizedImage - Fast-loading image component with:
 * - Native lazy loading
 * - Placeholder while loading
 * - Smooth fade-in transition
 * - Error handling with fallback
 */
const OptimizedImage = ({
  src,
  alt = '',
  className = '',
  fallbackIcon: FallbackIcon,
  fallbackClassName = '',
  placeholderColor = 'bg-gray-800',
  aspectRatio = 'aspect-square',
  objectFit = 'object-cover',
  ...props
}) => {
  const [isLoaded, setIsLoaded] = useState(false);
  const [hasError, setHasError] = useState(false);
  const imgRef = useRef(null);

  // Reset states when src changes
  useEffect(() => {
    setIsLoaded(false);
    setHasError(false);
  }, [src]);

  // Check if image is already cached
  useEffect(() => {
    if (imgRef.current?.complete && imgRef.current?.naturalHeight !== 0) {
      setIsLoaded(true);
    }
  }, [src]);

  const handleLoad = () => {
    setIsLoaded(true);
  };

  const handleError = () => {
    setHasError(true);
    setIsLoaded(true);
  };

  if (hasError || !src) {
    return (
      <div className={`${aspectRatio} ${placeholderColor} flex items-center justify-center ${className}`}>
        {FallbackIcon && <FallbackIcon className={fallbackClassName || 'text-gray-600'} size={24} />}
      </div>
    );
  }

  return (
    <div className={`relative ${aspectRatio} overflow-hidden ${className}`}>
      {/* Placeholder skeleton */}
      <div 
        className={`absolute inset-0 ${placeholderColor} transition-opacity duration-300 ${isLoaded ? 'opacity-0' : 'opacity-100'}`}
      >
        {/* Shimmer effect */}
        <div className="absolute inset-0 bg-gradient-to-r from-transparent via-gray-700/30 to-transparent animate-shimmer" />
      </div>
      
      {/* Actual image */}
      <img
        ref={imgRef}
        src={src}
        alt={alt}
        loading="lazy"
        decoding="async"
        onLoad={handleLoad}
        onError={handleError}
        className={`w-full h-full ${objectFit} transition-opacity duration-300 ${isLoaded ? 'opacity-100' : 'opacity-0'}`}
        {...props}
      />
    </div>
  );
};

/**
 * FastImage - Even simpler optimized image for lists
 * Uses native lazy loading without extra state management
 */
export const FastImage = ({
  src,
  alt = '',
  className = '',
  fallbackIcon: FallbackIcon,
  fallbackClassName = '',
  onError,
  ...props
}) => {
  const [hasError, setHasError] = useState(false);

  const handleError = (e) => {
    setHasError(true);
    onError?.(e);
  };

  if (hasError || !src) {
    return FallbackIcon ? (
      <div className={`w-full h-full bg-gray-800 flex items-center justify-center ${className}`}>
        <FallbackIcon className={fallbackClassName || 'text-gray-600'} size={24} />
      </div>
    ) : null;
  }

  return (
    <img
      src={src}
      alt={alt}
      loading="lazy"
      decoding="async"
      onError={handleError}
      className={`${className}`}
      {...props}
    />
  );
};

/**
 * VideoThumbnail - Optimized thumbnail component specifically for video cards
 * Features:
 * - Instant render for base64 data URIs (no network round-trip)
 * - Fast loading with eager priority
 * - Shimmer placeholder only shown briefly
 * - YouTube URL fallback chain: maxresdefault → hqdefault → sddefault
 */
export const VideoThumbnail = ({
  src,
  alt = '',
  className = '',
  fallbackIcon: FallbackIcon,
  iconSize = 32,
  eager = true, // Default to eager loading for immediate display
  ...props
}) => {
  const containerRef = useRef(null);
  const imgRef = useRef(null);

  // Base64 data URIs are already in memory — treat as instantly ready
  const isBase64 = src && src.startsWith('data:');

  // Default to visible=true for eager loading to avoid delay
  const [visible, setVisible]       = useState(eager || isBase64);
  const [isLoaded, setIsLoaded]     = useState(false);
  const [hasError, setHasError]     = useState(false);
  const [currentSrc, setCurrentSrc] = useState((eager || isBase64) ? src : null);

  // Intersection Observer: only used when eager=false
  useEffect(() => {
    if (eager || isBase64) return; // skip observer for eager loading
    if (!src) return;

    const el = containerRef.current;
    if (!el) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setVisible(true);
          observer.disconnect();
        }
      },
      { rootMargin: '200px' }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [src, isBase64, eager]);

  // Set src once visible
  useEffect(() => {
    if (visible && src && currentSrc !== src) {
      setHasError(false);
      setIsLoaded(false);
      setCurrentSrc(src);
    }
  }, [visible, src, currentSrc]);

  // Check if image is already cached (for instant display)
  useEffect(() => {
    if (imgRef.current?.complete && imgRef.current?.naturalHeight !== 0) {
      setIsLoaded(true);
    }
  }, [currentSrc]);

  const handleLoad = () => setIsLoaded(true);
  const handleError = () => {
    // YouTube maxresdefault may not exist — try hqdefault, then sddefault
    if (currentSrc && currentSrc.includes('youtube.com/vi/') && currentSrc.includes('maxresdefault')) {
      setCurrentSrc(currentSrc.replace('maxresdefault', 'hqdefault'));
      return;
    }
    if (currentSrc && currentSrc.includes('youtube.com/vi/') && currentSrc.includes('hqdefault')) {
      setCurrentSrc(currentSrc.replace('hqdefault', 'sddefault'));
      return;
    }
    setHasError(true);
    setIsLoaded(true);
  };

  return (
    <div ref={containerRef} className={`relative w-full h-full overflow-hidden bg-gray-800 ${className}`}>
      {/* Placeholder shown only while image is loading */}
      {!isLoaded && !hasError && (
        <div className="absolute inset-0 bg-gray-800">
          {FallbackIcon && (
            <div className="absolute inset-0 flex items-center justify-center">
              <FallbackIcon size={iconSize} className="text-gray-600" />
            </div>
          )}
        </div>
      )}

      {currentSrc && !hasError && (
        <img
          ref={imgRef}
          src={currentSrc}
          alt={alt}
          loading={eager ? "eager" : "lazy"}
          decoding="async"
          fetchpriority={eager ? "high" : "auto"}
          onLoad={handleLoad}
          onError={handleError}
          className={`w-full h-full object-cover transition-opacity duration-150 ${isLoaded ? 'opacity-100' : 'opacity-0'}`}
          {...props}
        />
      )}

      {hasError && FallbackIcon && (
        <div className="absolute inset-0 flex items-center justify-center bg-gray-800">
          <FallbackIcon size={iconSize} className="text-gray-600" />
        </div>
      )}
    </div>
  );
};

export default OptimizedImage;
